import { Menu } from '@mantine/core'
import type { SearchRequest } from '@medplum/core'
import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import { searchRequestAsObject } from '@seen/core/display'
import type { QueryTypes } from '@seen/core/search'
import type { MRT_TableOptions } from 'mantine-react-table'
import { type JSX, useMemo, useState } from 'react'
import { FhirQueryLoader } from '#src/components/core/FhirQueryLoader.tsx'
import type { ColumnConfig } from '#src/components/fhir-table/FhirMRTTable.constants.tsx'
import { ReactTable } from '#src/components/fhir-table/ReactTable.tsx'
import { ReactTableDeleteAction } from '#src/components/fhir-table/ReactTableDeleteAction.tsx'
import { MantineMenuItemMedplumLinkAction } from '#src/components/fhir-table/ReactTableMedplumLinkAction.tsx'
import { useFhirSearch } from '#src/hooks/useFhirSearch.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'

const DEFAULT_PAGE_SIZE = 50

type TableOptions<T extends ResourceType> = Omit<
  MRT_TableOptions<ExtractResource<T>>,
  'columns' | 'data'
>

interface FhirMRTTableProps<T extends ResourceType> {
  resourceType: T
  columnConfigs: ColumnConfig<ExtractResource<T>>[]
  filterQuery?: QueryTypes
  searchRequest?: SearchRequest
  tableOptions?: TableOptions<T>
  showAdminActions?: boolean
  enablePagination?: boolean
}

export function FhirMRTTable<T extends ResourceType>({
  resourceType,
  columnConfigs,
  filterQuery,
  searchRequest,
  tableOptions,
  showAdminActions: showAdminActionsOverride,
  enablePagination,
}: FhirMRTTableProps<T>): JSX.Element {
  const showAdminActionsDefault = usePortalStore(
    (state) => state.showAdminActions
  )
  const showAdminActions = showAdminActionsOverride ?? showAdminActionsDefault
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const search = useMemo<SearchRequest>(
    () => ({
      resourceType,
      count: pageSize,
      total: 'accurate' as const,
      sortRules: [
        {
          code: '_id',
          descending: true,
        },
      ],
      offset: pageIndex * pageSize,
      ...searchRequest,
    }),
    [pageIndex, pageSize, searchRequest, resourceType]
  )

  const tableOptionsWithDefaults = useMemo<TableOptions<T>>(
    () => ({
      positionActionsColumn: 'last' as const,
      enableRowActions: showAdminActions,
      renderRowActionMenuItems: ({
        row,
      }: {
        row: { original: ExtractResource<T> }
      }) => (
        <>
          <Menu.Label>Admin</Menu.Label>
          <MantineMenuItemMedplumLinkAction resource={row.original} />
          <ReactTableDeleteAction row={row.original} />
        </>
      ),
      onPaginationChange: (
        updater:
          | { pageIndex: number; pageSize: number }
          | ((prev: { pageIndex: number; pageSize: number }) => {
              pageIndex: number
              pageSize: number
            })
      ) => {
        if (typeof updater === 'function') {
          const newPagination = updater({ pageIndex, pageSize })
          setPageIndex(newPagination.pageIndex)
          setPageSize(newPagination.pageSize)
        } else {
          setPageIndex(updater.pageIndex)
          setPageSize(updater.pageSize)
        }
      },
      manualPagination: true,
      ...tableOptions,
    }),
    [showAdminActions, tableOptions, pageIndex, pageSize]
  )

  const searchQuery = useFhirSearch(resourceType, {
    ...searchRequestAsObject(search),
    ...filterQuery,
  })
  const rowCount = searchQuery.data?.bundle?.total ?? 0

  const paginationOptions: Partial<MRT_TableOptions<ExtractResource<T>>> = {
    enablePagination: true,
    manualPagination: true,
    enableBottomToolbar: true,
    paginationDisplayMode: 'pages',
    positionPagination: 'bottom',
  }

  const mergedTableOptions = {
    ...tableOptionsWithDefaults,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    rowCount,
  }
  if (enablePagination) {
    Object.assign(mergedTableOptions, paginationOptions)
  }

  return (
    <FhirQueryLoader query={searchQuery}>
      {(data) => (
        <ReactTable<ExtractResource<T>>
          columnConfigs={columnConfigs}
          data={data}
          tableOptions={mergedTableOptions}
        />
      )}
    </FhirQueryLoader>
  )
}
